import { useState, useEffect, useMemo } from "react";
import { useParams } from "@tanstack/react-router";
import { FileQuestion, Lock } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { CityReportHeader } from "@/components/city-review/CityReportHeader";
import { StationMap } from "@/components/city-review/StationMap";
import { StationTable } from "@/components/city-review/StationTable";
import { SportsDistributionPie } from "@/components/city-review/SportsDistributionPie";
import { StationDetailSidebar } from "@/components/city-review/StationDetailSidebar";
import { FirstVisitHint } from "@/components/city-review/FirstVisitHint";
import { Appendix } from "@/components/city-review/Appendix";

const CLIENT_ID_KEY = "city_review_client_id";

function getOrCreateClientId(): string {
  if (typeof window === "undefined") return "";
  try {
    const existing = localStorage.getItem(CLIENT_ID_KEY);
    if (existing) return existing;
    const next =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `cid-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(CLIENT_ID_KEY, next);
    return next;
  } catch {
    return `cid-${Date.now()}`;
  }
}

export function CityReportPage() {
  const params = useParams({ strict: false });
  const token = (params as { token?: string }).token ?? "";

  const [clientId, setClientId] = useState("");
  const [tab, setTab] = useState<"main" | "backup">("main");
  const [selectedStationId, setSelectedStationId] = useState<string | null>(null);

  useEffect(() => {
    setClientId(getOrCreateClientId());
  }, []);

  const { data, isLoading, error } = trpc.cityReport.getReport.useQuery(
    { token },
    { enabled: !!token },
  );

  const allStations = useMemo(() => {
    if (!data) return [];
    return [...data.mainStations, ...data.backupStations];
  }, [data]);

  const selectedStation = useMemo(() => {
    if (!selectedStationId) return null;
    return allStations.find((s) => s.id === selectedStationId) ?? null;
  }, [allStations, selectedStationId]);

  const activeStations = tab === "main" ? data?.mainStations ?? [] : data?.backupStations ?? [];
  const activeSports = tab === "main"
    ? data?.sportsDistribution ?? []
    : data?.backupSportsDistribution ?? [];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-16 w-16 rounded-lg" />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-4 w-40" />
            </div>
          </div>
          <Skeleton className="h-10 w-48" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Skeleton className="h-[45vh] lg:col-span-2" />
            <Skeleton className="h-[45vh]" />
          </div>
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    const isNotFound =
      error.data?.code === "NOT_FOUND" ||
      (error.message ?? "").toLowerCase().includes("not found");
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 px-6">
        <div className="text-center space-y-3 max-w-md">
          <FileQuestion className="size-12 text-muted-foreground mx-auto" />
          <h1 className="text-2xl font-semibold">
            {isNotFound ? "This report is not available." : "Something went wrong."}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isNotFound
              ? "The link may have expired or the report may have been removed."
              : "Please try again later."}
          </p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { collection, mainStations } = data;
  const readOnly = collection.closed;

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        <CityReportHeader
          token={token}
          collection={collection}
          mainStations={mainStations}
        />

        {readOnly && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
            <Lock className="size-4 mt-0.5 shrink-0" />
            <div>
              <strong className="font-medium">This report is closed.</strong>{" "}
              You can still browse stations, but verdicts and comments are read-only.
            </div>
          </div>
        )}

        <FirstVisitHint token={token} />

        <Tabs value={tab} onValueChange={(v) => setTab(v as "main" | "backup")}>
          <TabsList>
            <TabsTrigger value="main">
              Main <span className="ml-1 text-muted-foreground">({data.mainStations.length})</span>
            </TabsTrigger>
            <TabsTrigger value="backup">
              Backup <span className="ml-1 text-muted-foreground">({data.backupStations.length})</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value={tab} className="mt-4 space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 rounded-lg border bg-background overflow-hidden h-[45vh]">
                <StationMap
                  stations={activeStations}
                  selectedStationId={selectedStationId}
                  onSelect={setSelectedStationId}
                />
              </div>
              <div className="rounded-lg border bg-background p-4 h-[45vh] overflow-auto">
                <h3 className="text-sm font-medium mb-3">Sports distribution</h3>
                <SportsDistributionPie data={activeSports} />
              </div>
            </div>

            <div className="rounded-lg border bg-background p-4">
              <StationTable
                stations={activeStations}
                onSelect={setSelectedStationId}
                selectedStationId={selectedStationId}
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Appendix />

      <StationDetailSidebar
        station={selectedStation}
        token={token}
        clientId={clientId}
        readOnly={readOnly}
        onClose={() => setSelectedStationId(null)}
      />
    </div>
  );
}
